import { useState, useCallback, lazy, Suspense } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, ExternalLink, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface FooterItem {
  id: string;
  type: "text" | "link" | "button";
  content: string;
  url: string | null;
  sort_order: number;
}

// Lazy load the admin DnD footer content
const AdminFooterContent = lazy(() => import("@/components/AdminFooterContent"));

const Footer = () => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<FooterItem | null>(null);
  const [formData, setFormData] = useState({
    type: "text" as "text" | "link" | "button",
    content: "",
    url: "",
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["footer-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("footer_items")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as FooterItem[];
    },
    staleTime: 30 * 60_000, // Bootstrap seeds this cache — rarely refetch
    gcTime: 60 * 60_000,
  });

  const fetchItems = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["footer-items"] });
  }, [queryClient]);

  const openAddDialog = () => {
    setEditingItem(null);
    setFormData({ type: "text", content: "", url: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (item: FooterItem) => {
    setEditingItem(item);
    setFormData({
      type: item.type,
      content: item.content,
      url: item.url || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.content.trim()) {
      toast({
        title: "Błąd",
        description: "Treść nie może być pusta",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingItem) {
        const { error } = await supabase
          .from("footer_items")
          .update({
            type: formData.type,
            content: formData.content,
            url: formData.type !== "text" ? formData.url : null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingItem.id);

        if (error) throw error;
        toast({ title: "Zapisano", description: "Element został zaktualizowany" });
      } else {
        const maxOrder = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) : -1;
        const { error } = await supabase.from("footer_items").insert({
          type: formData.type,
          content: formData.content,
          url: formData.type !== "text" ? formData.url : null,
          sort_order: maxOrder + 1,
        });

        if (error) throw error;
        toast({ title: "Dodano", description: "Element został dodany do stopki" });
      }

      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error("Error saving footer item:", error);
      toast({
        title: "Błąd",
        description: "Nie udało się zapisać elementu",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("footer_items").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Usunięto", description: "Element został usunięty" });
      fetchItems();
    } catch (error) {
      console.error("Error deleting footer item:", error);
      toast({
        title: "Błąd",
        description: "Nie udało się usunąć elementu",
        variant: "destructive",
      });
    }
  };

  const renderItem = (item: FooterItem) => {
    if (item.type === "link" && item.url) {
      const isInternal = item.url.startsWith("/");
      if (isInternal) {
        return (
          <Link
            to={item.url}
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            {item.content}
          </Link>
        );
      }
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1"
        >
          {item.content}
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    }

    if (item.type === "button" && item.url) {
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        >
          {item.content}
        </a>
      );
    }

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: item.content.replace("❤️", '<span class="text-red-500">❤️</span>'),
        }}
      />
    );
  };

  if (isLoading) return null;

  return (
    <footer data-nosnippet className="w-full py-3 px-4 mt-auto border-t border-border/20">
      <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground/70">
        {isAdmin ? (
          <Suspense fallback={
            items.map((item, index) => (
              <div key={item.id} className="inline-flex items-center gap-2">
                {renderItem(item)}
                {index < items.length - 1 && <span className="text-border">•</span>}
              </div>
            ))
          }>
            <AdminFooterContent
              items={items}
              setItems={(newItems: FooterItem[]) => {
                queryClient.setQueryData(["footer-items"], newItems);
              }}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              renderItem={renderItem}
              fetchItems={fetchItems}
            />
          </Suspense>
        ) : (
          items.map((item, index) => (
            <div key={item.id} className="inline-flex items-center gap-2">
              {renderItem(item)}
              {index < items.length - 1 && <span className="text-border">•</span>}
            </div>
          ))
        )}

        {isAdmin && (
          <button
            onClick={openAddDialog}
            className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
            title="Dodaj element"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Edytuj element stopki" : "Dodaj element stopki"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select
                value={formData.type}
                onValueChange={(value: "text" | "link" | "button") =>
                  setFormData((prev) => ({ ...prev, type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Tekst</SelectItem>
                  <SelectItem value="link">Link</SelectItem>
                  <SelectItem value="button">Przycisk</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Treść</Label>
              <Input
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                placeholder="Wpisz treść..."
              />
            </div>

            {formData.type !== "text" && (
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  value={formData.url}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, url: e.target.value }))
                  }
                  placeholder="https://..."
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Anuluj
              </Button>
              <Button onClick={handleSave}>
                <Check className="w-4 h-4 mr-2" />
                Zapisz
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};

export default Footer;
